import { generatedTypesNew } from '@css-xp/graphtypes';
import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import { v4 as generateUuid } from 'uuid';
import { CCPPricing, PricingPlanType } from '@atlassian/cssxp-typescommon';
import { OrderItemType, EntitlementReferenceType } from '@atlassian/cssxp-types-entitlement';
import { getBillEstimateV2 } from '@atlassian/cssxp-proxies';
import { isJSMProduct, isJiraProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import type { OrderBillEstimateItemResponseDto } from '@atlassian/commerce-ccp-gateway-client';
import {
  isExistingStandaloneJPDProduct,
  isProductBelongsToJiraFamily,
  mapEstimate,
  getLinkedEntitlementChargeElement,
  JSM_PREMIUM_OFFERING_ID,
  isCollectionProductEntitlement,
  isStatusPageAddOnProduct,
} from '@css-xp/ppc-utils';
import { getChargeElement } from '@css-xp/base/entitlement-utils';
import type { BillEstimateResponse, CartLinkedEntitlementsSchema, CartProductSchema } from '../../types';
import {
  CloudSiteTypeEnum,
  OperationType,
  ProductStatus,
  Dictionary,
  PromotionTypes,
  LinkedEntitlementRelationShipType,
} from '../../types';
import type { State, GetOmsPayload } from '../types';
import { truthy } from '../../utils';
import { getTrialStatusForEntitlement } from './get-trial-status-for-entitlement';
import { isFreeOffering } from './is-free-offering';
import { getProductsAmendmentAndPatchPayloadFromTransfer } from './get-products-amendment-and-patch-payload-from-transfer';
import { getAppsAmendmentAndPatchPayloadFromTransfer } from './get-apps-amendment-and-patch-payload-from-transfer';
import {
  JIRA_RELATIONSHIP_OFFERING_KEY,
  PARTNER_DISCOUNT_TYPE,
  ROVO_PRODUCT_ID,
  JIRA_SANDBOX_RELATIONSHIP_OFFERING_KEY,
  ATLASSIAN_ACCESS_PRODUCT_ID,
  STATUSPAGE_PRODUCT_ID,
} from '@css-xp/ppc-constants';
import { isNewEntitlement } from './is-new-entitlement';
import { getLineItemsForDestinationBillingProfileChange } from './get-line-items-for-destination-billing-profile-change';
import { getTimestampFromLinkedProductsOrApps } from './get-timestamp-from-linked-products-apps';
import { getSandboxOmsPayload } from './get-sandbox-oms-payload';
import { getPricingPlanId } from './get-pricing-plan-id';
import { getExistingNextBillDateAnchorTimeFromCloudSite } from './existing-product-next-bill-date-time-for-cloudsite';

export enum OmsCapability {
  BILL_ESTIMATE = 'BILL_ESTIMATE',
  ORDER_PREVIEW = 'ORDER_PREVIEW',
  ORDER_PLACEMENT = 'ORDER_PLACEMENT',
}

enum SplitOrder {
  Y = 'Y',
  N = 'N',
}

const isFreeAppOffering = (app: CartLinkedEntitlementsSchema) => app?.offeringName?.toLowerCase().includes('free');

export type OmsPayload = Parameters<typeof getBillEstimateV2>[0];
// direct using CustomOrderEntitlement from package has version related issues
export type CustomOrderEntitlement = NonNullable<OmsPayload['entitlements']>[0] & {
  subscriptionStartTime?: number;
};

const CONTAINER_OFFERING_ID = JIRA_RELATIONSHIP_OFFERING_KEY;
const SANDBOX_CONTAINER_OFFERING_ID = JIRA_SANDBOX_RELATIONSHIP_OFFERING_KEY;
export const CONTAINER_PRODUCT_ID = '5a1a172d-651c-4c76-a5ec-70543564372c';

export function getIsCloudSiteSelected(state: State, cloudSiteName: string) {
  const allProducts = Object.values(state.products);
  const productsGroupedByCloudSite = groupBy(allProducts, 'cloudSite');
  const allSelectedProducts = state.selectedProducts;

  const productsInCloudSite = productsGroupedByCloudSite[cloudSiteName];
  if (!productsInCloudSite || !productsInCloudSite.length) {
    return false;
  }
  /**
   * As cloudsite selection behaves like a `Select ALL` for the group (products belonging to the cloudsite),
   * To determine if the cloudsite is selected, we must check if all of it's products are selected.
   */
  const cloudSiteSelectedProducts = productsInCloudSite.filter(({ id }) => allSelectedProducts[id]);
  const isCloudSiteChecked = cloudSiteSelectedProducts.length === productsInCloudSite.length;

  return isCloudSiteChecked;
}

export function groupByCloudSiteOrOrgName(product: CartProductSchema) {
  return product.organisationName ? product.organisationName : product.cloudSite;
}

export function getIsOrganisationSelected(state: State, orgName: string) {
  const allProducts = Object.values(state.products);
  const allOrgProducts = allProducts?.filter((product) => product?.organisationName);
  const productsGroupedByOrgName = groupBy(allOrgProducts, 'organisationName');
  const allSelectedProducts = state.selectedProducts;

  const productsInOrg = productsGroupedByOrgName[orgName];

  const orgSelectedProducts = productsInOrg?.filter(({ id }) => allSelectedProducts[id]);
  let isOrgChecked = false;
  if (productsInOrg && orgSelectedProducts) {
    isOrgChecked = orgSelectedProducts.length === productsInOrg.length;
  }

  return isOrgChecked;
}

/**
 * Use this for verifying if the entire products inside a cloud site URL or org are selected
 * Test cases covered in a separate file,
 * @param state
 * @param cloudSiteOrOrgName
 */
export function isCloudSiteOrOrganisationSelected(state: State, cloudSiteOrOrgName: string) {
  const allProducts = Object.values(state.products);
  const productsGroupedByOrgNameOrCloudSite = groupBy(allProducts, groupByCloudSiteOrOrgName);
  const allSelectedProducts = state.selectedProducts;

  const productsInOrgOrCloudSite = productsGroupedByOrgNameOrCloudSite[cloudSiteOrOrgName];

  const orgOrCloudSiteSelectedProducts = productsInOrgOrCloudSite?.filter(({ id }) => allSelectedProducts[id]);
  let isChecked = false;
  if (productsInOrgOrCloudSite && orgOrCloudSiteSelectedProducts) {
    isChecked = orgOrCloudSiteSelectedProducts.length === productsInOrgOrCloudSite.length;
  }

  return isChecked;
}

/**
 * This fn has been pulled out of `useProcessLineItemsForOrderIntentEstimate`'s `processLineItemsForOrderEstimate` method as is with 2 minor modifications: we use `entitlementId` as unique identifier and the return is vanilla JS objects (and not es6 maps)
 */

export const processBillEstimateResponse = ({
  billEstimateResponse,
  cartProducts,
  cartApps,
}: {
  billEstimateResponse: OrderBillEstimateItemResponseDto;
  cartProducts: CartProductSchema[];
  cartApps: CartLinkedEntitlementsSchema[];
}): BillEstimateResponse => {
  const mappedEstimate = mapEstimate(billEstimateResponse);
  const estimates = new Map();
  const taxItemsMap = new Map<
    string,
    {
      tax: number;
      taxPercent: number;
    }
  >();
  const adminTaxDetailsMap = new Map<
    string,
    {
      adminTaxItemLabel: string;
      totalAdminTax: number;
    }
  >();
  const processLineItem = (lineItem: CartProductSchema) => {
    const relatedEstimateItem = mappedEstimate.items.find((item) => {
      if (!item?.itemId || !lineItem.id) return;
      return item.itemId === lineItem.id || item.itemId === lineItem.itemId;
    });

    const typeOfAdjustments = [
      ...new Set(relatedEstimateItem?.partnerPricing?.adjustments?.map((adjustment) => adjustment?.type)),
    ];
    const allPromotionsApplied = typeOfAdjustments.map((typeOfAdjustment) => {
      const adjustments = relatedEstimateItem?.partnerPricing?.adjustments?.filter(
        (adjustment) => adjustment.type === typeOfAdjustment
      );
      const percent = adjustments?.[0]?.percent;
      const totalAdjustment = adjustments?.reduce((acc, adjustment) => {
        return acc + (adjustment?.amount ?? 0);
      }, 0);

      return {
        typeOfAdjustment,
        adjustmentLabel: typeOfAdjustment === 'PROMO_CODE' ? 'Vendor discount' : 'Partner promotion',
        totalAdjustment,
        percent,
      };
    });
    const estimateDetails = {
      grandTotal: relatedEstimateItem?.partnerPricing.grandTotal,
      listPrice: relatedEstimateItem?.partnerPricing.listPrice,
      totalDiscount: relatedEstimateItem?.partnerPricing.totalDiscount,
      totalOtherDiscount: relatedEstimateItem?.partnerPricing.totalOtherDiscount,
      totalPartnerDiscount: relatedEstimateItem?.partnerPricing.totalPartnerDiscount,
      discountPercent: relatedEstimateItem?.partnerPricing?.adjustments?.[0]?.percent,
      total: relatedEstimateItem?.partnerPricing.totalExcludingTax,
      totalTax: relatedEstimateItem?.partnerPricing.totalTax,
      estimateIncludingPartnerDiscount: relatedEstimateItem?.partnerPricing?.totalPriceAfterDiscountAndBeforeTaxes,
      allPromotionsApplied,
      isAdminFeeApplicable: relatedEstimateItem?.isAdminFeeApplicable,
      ...(relatedEstimateItem?.isAdminFeeApplicable && {
        adminFee: relatedEstimateItem?.adminFee?.amount,
        percentageOfAdminFee: relatedEstimateItem?.percentageOfAdminFee,
      }),
    };
    relatedEstimateItem?.partnerPricing.taxItems.forEach((taxItem) => {
      if (taxItem.taxAmountLabel) {
        const taxValue = taxItem?.tax ?? 0;
        if (taxItemsMap.has(taxItem.taxAmountLabel)) {
          const currentTaxValueForCurrentLabel = taxItemsMap.get(taxItem.taxAmountLabel);
          if (currentTaxValueForCurrentLabel) {
            taxItemsMap.set(taxItem.taxAmountLabel, {
              ...currentTaxValueForCurrentLabel,
              tax: (currentTaxValueForCurrentLabel?.tax ?? 0) + taxValue,
            });
          }
          return;
        }
        taxItemsMap.set(taxItem.taxAmountLabel, {
          tax: taxValue,
          taxPercent: taxItem.taxPercent ?? 0,
        });
      }
    });
    const uniqueTaxLabels = new Set<string>();
    const taxLabelWiseTaxInfo = relatedEstimateItem?.partnerPricing.taxItems.reduce<
      Record<
        string,
        {
          tax: number;
          taxPercent: number;
        }
      >
    >((acc, taxInfo) => {
      if (taxInfo.taxAmountLabel) {
        uniqueTaxLabels.add(taxInfo.taxAmountLabel);
        if (acc[taxInfo.taxAmountLabel]) {
          acc[taxInfo.taxAmountLabel] = {
            tax: acc[taxInfo.taxAmountLabel].tax + (taxInfo?.tax ?? 0),
            taxPercent: taxInfo.taxPercent ?? 0,
          };
          return acc;
        }
        acc[taxInfo.taxAmountLabel] = {
          tax: taxInfo.tax ?? 0,
          taxPercent: taxInfo.taxPercent ?? 0,
        };
        return acc;
      }
      return acc;
    }, {});
    estimates.set(lineItem.id, {
      ...estimateDetails,
      ...{
        itemLevelTaxInfo: taxLabelWiseTaxInfo
          ? [...uniqueTaxLabels].map((taxLabel) => {
              return [taxLabel, taxLabelWiseTaxInfo[taxLabel]];
            })
          : [],
      },
    });
    if (relatedEstimateItem?.partnerPricing.dstTaxItems.length) {
      const adminTaxItemLabel = relatedEstimateItem?.partnerPricing.dstTaxItems[0].taxAmountLabel;
      if (adminTaxItemLabel) {
        adminTaxDetailsMap.set(lineItem.id, { adminTaxItemLabel, totalAdminTax: 0 });
        const totalAdminTax = relatedEstimateItem?.partnerPricing.dstTaxItems.reduce((accumulator, { tax = 0 }) => {
          return accumulator + tax;
        }, 0);
        adminTaxDetailsMap.set(lineItem.id, { adminTaxItemLabel, totalAdminTax });
      }
    }
  };
  cartProducts.forEach((lineItem) => {
    processLineItem(lineItem);
    const lineItemEstimate = estimates.get(lineItem.id);
    if (lineItemEstimate) {
      estimates.set(lineItem.id, {
        ...lineItemEstimate,
      });
    }
  });

  const processAppLineItem = (lineItem: CartLinkedEntitlementsSchema) => {
    const relatedEstimateItem = mappedEstimate?.items.find((item) => item.itemId === lineItem.id);
    const partnerPricingInfo = relatedEstimateItem?.partnerPricing;
    const typeOfAdjustments = [...new Set(partnerPricingInfo?.adjustments?.map((adjustment) => adjustment?.type))];
    const allPromotionsApplied = typeOfAdjustments.map((typeOfAdjustment) => {
      const adjustments = partnerPricingInfo?.adjustments?.filter((adjustment) => adjustment.type === typeOfAdjustment);
      const percent = adjustments?.[0]?.percent;
      const totalAdjustment = adjustments?.reduce((acc, adjustment) => {
        return acc + (adjustment?.amount ?? 0);
      }, 0);

      return {
        typeOfAdjustment,
        adjustmentLabel: typeOfAdjustment === 'PROMO_CODE' ? 'Vendor discount' : 'Partner promotion',
        totalAdjustment,
        percent,
      };
    });
    const estimateDetails = {
      grandTotal: partnerPricingInfo?.grandTotal,
      listPrice: partnerPricingInfo?.listPrice,
      totalDiscount: partnerPricingInfo?.totalDiscount,
      totalPartnerDiscount: partnerPricingInfo?.totalPartnerDiscount,
      totalOtherDiscount: partnerPricingInfo?.totalOtherDiscount,
      discountPercent: partnerPricingInfo?.adjustments?.[0]?.percent,
      total: partnerPricingInfo?.totalExcludingTax,
      totalTax: partnerPricingInfo?.totalTax,
      estimateIncludingPartnerDiscount: partnerPricingInfo?.totalPriceAfterDiscountAndBeforeTaxes,
      allPromotionsApplied,
      isAdminFeeApplicable: relatedEstimateItem?.isAdminFeeApplicable,
      ...(relatedEstimateItem?.isAdminFeeApplicable && {
        adminFee: relatedEstimateItem?.adminFee?.amount,
        percentageOfAdminFee: relatedEstimateItem?.percentageOfAdminFee,
      }),
    };
    partnerPricingInfo?.taxItems.forEach((taxItem) => {
      if (taxItem.taxAmountLabel) {
        const taxValue = taxItem?.tax ?? 0;
        if (taxItemsMap.has(taxItem.taxAmountLabel)) {
          const currentTaxValueForCurrentLabel = taxItemsMap.get(taxItem.taxAmountLabel);
          if (currentTaxValueForCurrentLabel) {
            taxItemsMap.set(taxItem.taxAmountLabel, {
              ...currentTaxValueForCurrentLabel,
              tax: (currentTaxValueForCurrentLabel?.tax ?? 0) + taxValue,
            });
          }
          return;
        }
        taxItemsMap.set(taxItem.taxAmountLabel, {
          tax: taxValue,
          taxPercent: taxItem.taxPercent ?? 0,
        });
      }
    });
    const uniqueTaxLabels = new Set<string>();
    const taxLabelWiseTaxInfo: Record<
      string,
      {
        tax: number;
        taxPercent: number;
      }
    > = {};

    partnerPricingInfo?.taxItems.forEach((taxInfo) => {
      if (!taxInfo.taxAmountLabel) return;

      const taxAmountLabelInfo = taxInfo.taxAmountLabel;
      uniqueTaxLabels.add(taxAmountLabelInfo);
      if (!taxLabelWiseTaxInfo[taxAmountLabelInfo]) {
        taxLabelWiseTaxInfo[taxAmountLabelInfo] = {
          tax: taxInfo.tax ?? 0,
          taxPercent: taxInfo.taxPercent ?? 0,
        };
      } else {
        const currentTaxValue = taxLabelWiseTaxInfo[taxAmountLabelInfo].tax;
        const updatedTaxValue = currentTaxValue + (taxInfo?.tax ?? 0);

        taxLabelWiseTaxInfo[taxAmountLabelInfo] = {
          tax: updatedTaxValue,
          taxPercent: taxInfo.taxPercent ?? 0,
        };
      }
    });

    estimates.set(lineItem.id, {
      ...estimateDetails,
      ...{
        itemLevelTaxInfo: taxLabelWiseTaxInfo
          ? [...uniqueTaxLabels].map((taxLabel) => {
              return [taxLabel, taxLabelWiseTaxInfo[taxLabel]];
            })
          : [],
      },
    });
    if (partnerPricingInfo?.dstTaxItems.length) {
      const adminTaxItemLabel = partnerPricingInfo.dstTaxItems[0].taxAmountLabel;
      if (adminTaxItemLabel) {
        adminTaxDetailsMap.set(lineItem.id, { adminTaxItemLabel, totalAdminTax: 0 });
        const totalAdminTax = partnerPricingInfo.dstTaxItems.reduce((accumulator, { tax = 0 }) => {
          return accumulator + tax;
        }, 0);
        adminTaxDetailsMap.set(lineItem.id, { adminTaxItemLabel, totalAdminTax });
      }
    }
  };
  cartApps.forEach((lineItem) => {
    processAppLineItem(lineItem);
    const lineItemEstimate = estimates.get(lineItem.id);
    if (lineItemEstimate) {
      estimates.set(lineItem.id, {
        ...lineItemEstimate,
      });
    }
  });

  const partnerPricing = mappedEstimate.partnerPricing;

  return {
    partnerPricing,
    estimates: Object.fromEntries(estimates),
    taxItemsMap: Object.fromEntries(taxItemsMap),
    adminTaxDetailsMap: Object.fromEntries(adminTaxDetailsMap),
  };
};

const getRelatesFromSandboxDepenedencyForApp = (
  sandboxParentData: State['sandboxParentData'],
  cloudSiteId: string,
  appProductId: string
) => {
  if (!sandboxParentData?.[cloudSiteId]) return [];
  const productIdEntitlementIdMap = sandboxParentData[cloudSiteId];
  const appEntitlementId = productIdEntitlementIdMap[appProductId];
  if (!appEntitlementId) return [];
  return [
    {
      entitlementId: appEntitlementId,
      referenceType: EntitlementReferenceType.ENTITLEMENT,
      relationshipType: generatedTypesNew.RelationshipPathType.SANDBOX_DEPENDENCE,
    },
  ];
};

const isRequiredPromotion = (
  promotion: generatedTypesNew.Promotion,
  {
    excludeExpired,
  }: {
    excludeExpired: boolean;
  }
) => {
  return (
    promotion?.type !== PromotionTypes.PARTNER_DISCOUNT &&
    (excludeExpired && promotion?.status ? promotion.status.toLowerCase() === 'active' : true)
  );
};
async function getAppsPayload({
  initialStoreProducts,
  linkedEntitlements = [],
  omsCapability,
  parentProduct,
  selectedLinkedEntitlements,
  partnerPromotions,
  nextBillDate,
  currentPricingEligibility,
  offeringsAndPricingPlans,
  currentCurrency,
  isNonCommercialPricingEnabled,
  isCoTermInUofEnabled,
  isSandboxAppCreateInUOFEnabled,
  sandboxParentData,
  isExcludeExpiredPromotionEnabled,
  isAppEditionSupportEnabled,
}: {
  initialStoreProducts?: State['products'];
  linkedEntitlements: CartLinkedEntitlementsSchema[];
  omsCapability: OmsCapability;
  parentProduct: CartProductSchema;
  selectedLinkedEntitlements: State['selectedLinkedEntitlements'];
  partnerPromotions?: State['partnerPromotions'];
  nextBillDate?: State['nextBillDate'];
  currentPricingEligibility?: string;
  offeringsAndPricingPlans?: State['offeringsAndpricingPlans'];
  currentCurrency?: `${generatedTypesNew.Currency}`;
  isNonCommercialPricingEnabled?: boolean;
  isCoTermInUofEnabled?: boolean;
  isSandboxAppCreateInUOFEnabled?: boolean;
  sandboxParentData?: State['sandboxParentData'];
  isExcludeExpiredPromotionEnabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
}): Promise<OmsPayload['entitlements']> {
  const APP_DEPENDENCE = generatedTypesNew.RelationshipPathType.APP_DEPENDENCE;

  // @ts-expect-error FIXME: If we pass promotionId as null we get types error
  return Promise.all(
    linkedEntitlements.map(async (app) => {
      const isNew = app.operationType === OperationType.NEW;
      const isTransfer = app.operationType === OperationType.TRANSFER;
      const isTransferAndAmendment = isTransfer && app.productStatuses?.includes(ProductStatus.EDITED);
      const isBillEstimate = omsCapability === OmsCapability.BILL_ESTIMATE;
      const isBillEstimateForTransfer = isTransfer && omsCapability === OmsCapability.BILL_ESTIMATE;

      let entitlementId = (isNew || isBillEstimateForTransfer ? null : app.id) as string;
      const relatesFromEntitlementId = app?.parentEntitlementId;
      const isAddOn = app?.relationshipType === LinkedEntitlementRelationShipType.ADD_ON;
      const isApp = app?.relationshipType === LinkedEntitlementRelationShipType.APP;
      const selectedAppDetails = { ...app, ...(selectedLinkedEntitlements?.[app.id] || {}) };
      const selectedOfferingId = isAppEditionSupportEnabled ? selectedAppDetails?.offeringId : app.offeringId;

      // handling the case when sandbox app being added has parent product with same app
      // in such case we need to add relatedFrom for the container of family as well as app dependence
      let relatesFromArgForSandboxApp: {
        entitlementId: string;
        referenceType: EntitlementReferenceType;
        relationshipType: generatedTypesNew.RelationshipPathType;
      }[] = [];
      if (
        isSandboxAppCreateInUOFEnabled &&
        parentProduct?.isSandbox &&
        isNew &&
        parentProduct?.cloudSiteId &&
        app?.productId &&
        sandboxParentData
      ) {
        relatesFromArgForSandboxApp = getRelatesFromSandboxDepenedencyForApp(
          sandboxParentData,
          parentProduct.cloudSiteId,
          app.productId
        );
      }
      const orderType = (() => {
        if (isNew || isBillEstimateForTransfer) return OrderItemType.CREATION_ORDER;

        if (isTransfer) return OrderItemType.ACCOUNT_MODIFICATION_ORDER;

        return OrderItemType.AMENDMENT_ORDER;
      })();

      const nextBillingAnchorTimestamp =
        omsCapability !== OmsCapability.BILL_ESTIMATE
          ? await getNextBillingAnchorTimestampPayload({
              initialStoreProducts,
              app,
              nextBillDate,
              selectedLinkedEntitlements,
              parentProduct,
              isCoTermInUofEnabled,
            })
          : null;
      const nextBillingAnchorTimestampArg = nextBillingAnchorTimestamp
        ? {
            nextBillingAnchorTimestamp: nextBillingAnchorTimestamp,
          }
        : {};
      const filteredAppliedPartnerPromotions =
        // check to add existing promotions only if its bill estimate and not the new entitlement
        // filtering out the promotions that are already applied on existing apps which are expired
        isBillEstimate && !isNew && app?.promotions?.length
          ? app.promotions.filter((promotion) =>
              isRequiredPromotion(promotion, { excludeExpired: !!isExcludeExpiredPromotionEnabled })
            )
          : [];
      // Merging both existing promotions and new promotions
      let appliedPromotions = [
        ...filteredAppliedPartnerPromotions,
        ...(selectedLinkedEntitlements?.[app.id]?.promotions ?? []),
      ];
      const promotionsToRemove = [...(selectedLinkedEntitlements?.[app.id]?.removePromotions ?? [])];
      const partnerPromotionId = partnerPromotions?.[app.productId];

      const removePromotions = promotionsToRemove
        ?.filter((promotion) => !isEmpty(promotion))
        ?.map((promotion) => {
          return { promotionInstanceId: promotion?.promotionInstanceId };
        });

      if (omsCapability === OmsCapability.BILL_ESTIMATE) {
        appliedPromotions = appliedPromotions?.filter((promotion) => {
          const isPromotionToRemove = promotionsToRemove?.some(
            (promo) => promo?.promotionInstanceId === promotion?.promotionInstanceId
          );
          return !isPromotionToRemove;
        });
      }

      const addPromotions = appliedPromotions
        ?.filter((promotion) => !isEmpty(promotion))
        ?.map((promotion) => {
          /*
          promotion?.promocode is added by newly added promocode flow
          promotion?.promotionCode is coming from existing applied promotion
         */
          const promocode = promotion?.promocode ?? promotion?.promotionCode;
          const promotionId = promotion?.id;
          return {
            promotionInstanceId: null,
            promotionDefinition: {
              promotionId: !promocode ? promotionId : null, //We should not pass promotion id when applied with promocode
              promotionCode: promocode ?? null,
              customisedValues: null,
            },
          };
        });

      const offerings = offeringsAndPricingPlans?.[app.productId] || [];
      const offeringDetails = offerings?.find?.(({ id: offeringId }) => offeringId === app.offeringId);
      const isOfferingFree = isFreeOffering(offeringDetails?.pricingType ?? '');

      // For new app if app has eligible partner promotion id we need to get bill estimate for the same to show as partner margin
      if (
        partnerPromotionId &&
        !isFreeAppOffering(app) &&
        !isOfferingFree &&
        (app?.operationType === OperationType.NEW || omsCapability === OmsCapability.BILL_ESTIMATE)
      ) {
        addPromotions.push({
          promotionInstanceId: null,
          promotionDefinition: {
            promotionId: partnerPromotionId,
            promotionCode: null,
            customisedValues: null,
          },
        });
      }

      const metadata = (() => {
        // Ref. Doc - https://hello.atlassian.net/wiki/spaces/~686306312/pages/3044230860/EPIC+Pager+Patch+promotions+for+Transfer+Amendment+orders
        // Sending this flag to prevent POE make changes on entitlement
        // Pass AccntModificationCumAmendmentFlow as true to skip POE processing for D2P (direct to partner) or P2P (partner to partner) transfer
        const transferAmendmentPayloadArg = {
          AccntModificationCumAmendmentFlow: 'true',
        };
        if (isTransfer) {
          const POELineItemArg = app.isPartnerEntitlement ? { POELineItem: 'true' } : {};
          return {
            sourceTransactionAccountId: parentProduct.sourceTransactionAccountId as string,
            sourceInvoiceGroupId: parentProduct.sourceInvoiceGroupId ?? app.sourceInvoiceGroupId ?? '',
            // pass site id as metadata attribute to OMS engine
            siteId: parentProduct.cloudSiteId,
            ...transferAmendmentPayloadArg,
            ...POELineItemArg,
          };
        }

        /**
         * We need to send POELineItem: 'true' for the new app which are being added to
         * the existing products selected to transfer and the transferred product is a partner entitlement.
         */
        if (
          parentProduct.cloudSiteId &&
          parentProduct.isPartnerEntitlement &&
          parentProduct.operationType === OperationType.TRANSFER &&
          orderType === OrderItemType.CREATION_ORDER
        ) {
          return {
            siteId: parentProduct.cloudSiteId,
            POELineItem: 'true',
          };
        }

        if (parentProduct.cloudSiteId) {
          return {
            // pass site id as metadata attribute to OMS engine
            siteId: parentProduct.cloudSiteId,
          };
        }

        return {};
      })();

      const isParentNew = isNewEntitlement({ operationType: parentProduct.operationType });
      const relatesFromEntitlementIdArg = isParentNew ? {} : { entitlementId: app.containerOrProductId };

      /**
       * When we are calling getBillEstimate api, we need to send chargeQuantity for apps whichever is highest in container,
       * Or derived from parent entitlement for standalone products. And not 1 always. This is because on order page, we see
       * wrong grand total for apps when active user count is more than 1.
       */
      let chargeQuantity;
      if (OmsCapability.BILL_ESTIMATE === omsCapability) {
        chargeQuantity = Number(app.activeUsers);
      } else {
        chargeQuantity = 1;
      }

      const pricingPlanIdBasedOnPricingEligibilityAndOffering = (() => {
        if (
          !currentPricingEligibility ||
          !app.productId ||
          !offeringsAndPricingPlans ||
          !offeringsAndPricingPlans[app.productId]
        ) {
          return app.pricingPlanId;
        }
        const offerings = offeringsAndPricingPlans[app.productId];
        const pricingPlanId = getPricingPlanId({
          offerings,
          offeringId: selectedOfferingId,
          currency: currentCurrency,
          pricingEligibility: currentPricingEligibility as `${PricingPlanType}`,
          defaultPricingEligibilityToCommercial: isNonCommercialPricingEnabled,
          enableRetainPricingPlan: !!isNonCommercialPricingEnabled,
          initialPricingPlanId: app.pricingPlanId,
          initialOfferingId: app.offeringId,
          pricingPlanType: app.pricingPlanType,
          entitlementPriceEligibility: app.priceEligibility,
        });
        return pricingPlanId || app.pricingPlanId;
      })();

      const relatesFromLinkedEntitlementArgs = (() => {
        if (isAddOn) {
          /**
           * we are finding the valid parent product for the add-on product
           * checking if the add-on product is linked to JSM product with container id or atlassian guard product id
           * and if the parent product is new
           */
          const validAddOnParentProduct = Object.values(initialStoreProducts ?? {}).find(
            (product) =>
              (product?.containerId === app?.containerOrProductId && isJSMProduct(product?.productId)) ||
              (product.id === app?.containerOrProductId && product.productId === ATLASSIAN_ACCESS_PRODUCT_ID) ||
              (isStatuspage(product.productId) && app.containerOrProductId === product.id)
          );
          const isAddOnParentNew = validAddOnParentProduct && isNewEntitlement(validAddOnParentProduct);

          // there is a condition for statuspage add-on, where we need to send the entitlement id of the parent product
          // addon entitlement should be existing parent entitlementId if parent is not new and addon is new
          if (isStatuspage(validAddOnParentProduct?.productId) && !isAddOnParentNew && isNew) {
            entitlementId = validAddOnParentProduct?.id as string;
          }

          const addOnEntitlementIdArg = isAddOnParentNew
            ? { itemId: validAddOnParentProduct?.id, referenceType: EntitlementReferenceType.ORDER_ITEM }
            : {
                itemId: validAddOnParentProduct?.itemId,
                entitlementId: validAddOnParentProduct?.id,
                referenceType: EntitlementReferenceType.ENTITLEMENT,
              };
          return [
            {
              ...addOnEntitlementIdArg,
              relationshipType: 'ADDON_DEPENDENCE',
            },
          ];
        }
        return [
          {
            itemId: isParentNew ? app.containerOrProductId : parentProduct.itemId, // when net new products are added, the `containerOrProductId` is a UUID we use as `itemId` in the entitlement payload
            ...relatesFromEntitlementIdArg,
            referenceType: isParentNew ? EntitlementReferenceType.ORDER_ITEM : EntitlementReferenceType.ENTITLEMENT,
            relationshipType: APP_DEPENDENCE,
          },
        ];
      })();

      const skipTrialArgs = (() => {
        if (isNew) {
          if (isAddOn) {
            /**
             * We have to send `skipTrial: true` for add-on entitlements as currently add-ons offerings are not supporting for trial
             */
            return { skipTrial: true };
          }
          /**
           * As we are only allowing addition of promocode to existing apps,
           * We will be sending `skipTrial: false` only for the net new apps (irrespective of whether they are linked to paid, in trial or net new products).
           * For existing apps, CCP is able to derive the app's ongoing state (Trial or Paid) during order creation.
           * */
          return { skipTrial: false };
        } else if (isApp && isAppEditionSupportEnabled) {
          const skipTrialArg = getSkipTrialPayload({
            initialOfferingId: app?.offeringId,
            updatedOfferingId: selectedAppDetails?.offeringId,
            productId: app?.productId,
            isCurrentlyInTrial: app.inTrial === true,
            offeringsAndpricingPlans: offeringsAndPricingPlans || {},
            omsCapability: omsCapability,
          });

          return skipTrialArg;
        }

        return {};
      })();

      /**
       * we are converting productId = offeringId for statuspage addons and saving in store.
       * we are making this change because all statuspage addons have same product id
       * while placing an order again we revert productId with STATUSPAGE_PRODUCT_ID
       * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
       */
      const productId = isStatusPageAddOnProduct(app.productId) ? STATUSPAGE_PRODUCT_ID : app.productId;
      return {
        entitlementId: entitlementId,
        itemId: app.id,
        productId,
        ...skipTrialArgs,
        orderType: orderType,
        offeringId: selectedOfferingId,
        addPromotions,
        chargeQuantity: chargeQuantity,
        removePromotions,
        chargeElement: getLinkedEntitlementChargeElement(productId),
        relatesFromEntitlements: [...relatesFromLinkedEntitlementArgs, ...relatesFromArgForSandboxApp],
        metadata,
        pricingPlanId: pricingPlanIdBasedOnPricingEligibilityAndOffering,
        ...nextBillingAnchorTimestampArg,
      };
    })
  );
}

export async function getAppsForOms({
  initialStoreProducts,
  cartProducts,
  cartApps,
  omsCapability,
  selectedLinkedEntitlements,
  partnerPromotions,
  nextBillDate,
  currentPricingEligibility,
  offeringsAndPricingPlans,
  currentCurrency,
  isNonCommercialPricingEnabled,
  isCoTermInUofEnabled,
  isSandboxAppCreateInUOFEnabled,
  sandboxParentData,
  isExcludeExpiredPromotionEnabled,
  isAppEditionSupportEnabled,
}: {
  initialStoreProducts?: State['products'];
  cartProducts: CartProductSchema[];
  cartApps: State['linkedEntitlements'];
  omsCapability: OmsCapability;
  selectedLinkedEntitlements: State['selectedLinkedEntitlements'];
  partnerPromotions?: State['partnerPromotions'];
  nextBillDate?: State['nextBillDate'];
  currentPricingEligibility?: string;
  offeringsAndPricingPlans?: State['offeringsAndpricingPlans'];
  currentCurrency?: `${generatedTypesNew.Currency}`;
  isNonCommercialPricingEnabled?: boolean;
  isCoTermInUofEnabled?: boolean;
  isSandboxAppCreateInUOFEnabled?: boolean;
  sandboxParentData?: State['sandboxParentData'];
  isExcludeExpiredPromotionEnabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
}): Promise<OmsPayload['entitlements']> {
  const idOrContainerIdToProductMap = keyBy(cartProducts, ({ id, containerId }) => containerId || id);
  const idOrContainerIdToProductGroupBy = groupBy(cartProducts, ({ id, containerId }) => containerId || id);
  const productOrContainerIds = Object.keys(idOrContainerIdToProductMap);

  const containerGroupedAppsPayloadArr = await Promise.all(
    productOrContainerIds.map(async (productOrContainerId) => {
      const linkedEntitlementsToProcess: CartLinkedEntitlementsSchema[] = (() => {
        const productOrContainerIdApps = cartApps[productOrContainerId];

        /**
         * we are finding the valid parent product for the add-on product
         * checking if the add-on product is linked to JSM product with container id or atlassian guard product id
         */
        const validOnLinkedProduct = idOrContainerIdToProductGroupBy[productOrContainerId]?.find(
          (product) =>
            (isJSMProduct(product.productId) && product?.offeringId === JSM_PREMIUM_OFFERING_ID) ||
            product.productId === ATLASSIAN_ACCESS_PRODUCT_ID ||
            isStatuspage(product.productId)
        );
        const filteredLinkedEntitlements = (() => {
          /**
           * if the product is a container family and container has no JSM product, we need to filter out the add-on
           * if the container has JSM product and it is not premium, we need to filter out the add-on
           */
          if (!validOnLinkedProduct) {
            return productOrContainerIdApps?.filter(
              (app) => app.relationshipType !== LinkedEntitlementRelationShipType.ADD_ON
            );
          }
          return productOrContainerIdApps?.filter(
            (app) => !selectedLinkedEntitlements?.[app.id]?.productStatuses?.includes(ProductStatus.CANCEL)
          );
        })();
        // NOTE: Here we are differentiating payload for bill estimate and order preview. But oms payload should always be same for all oms api call.
        // In the future, we have to fix this issue.
        if (omsCapability === OmsCapability.ORDER_PREVIEW) {
          // For order preview, We are allowing NEW and TRANSFER and Edited apps to place an order.
          return filteredLinkedEntitlements?.filter(
            (linkedEntitlement) =>
              [OperationType.NEW, OperationType.TRANSFER].includes(linkedEntitlement.operationType) ||
              selectedLinkedEntitlements?.[linkedEntitlement.id]?.productStatuses?.includes(ProductStatus.EDITED)
          );
        }
        return filteredLinkedEntitlements;
      })();

      if (!linkedEntitlementsToProcess) return;

      const parentProduct = (() => {
        // If the product is a container family, we need to find the existing Jira product in the container
        const existingJiraParentProduct = Object.values(cartProducts).find(
          (product) => product.containerId === productOrContainerId && !isNewEntitlement(product)
        );
        if (existingJiraParentProduct) {
          return existingJiraParentProduct;
        }
        return idOrContainerIdToProductMap[productOrContainerId];
      })();

      const appsPayload = await getAppsPayload({
        initialStoreProducts,
        linkedEntitlements: linkedEntitlementsToProcess,
        omsCapability,
        parentProduct,
        selectedLinkedEntitlements,
        partnerPromotions,
        nextBillDate,
        currentPricingEligibility,
        offeringsAndPricingPlans,
        currentCurrency,
        isNonCommercialPricingEnabled,
        isCoTermInUofEnabled,
        isSandboxAppCreateInUOFEnabled,
        sandboxParentData,
        isExcludeExpiredPromotionEnabled,
        isAppEditionSupportEnabled,
      });

      const appsAmendmentPayloadForTransfer = getAppsAmendmentAndPatchPayloadFromTransfer({
        appsPayload,
        selectedLinkedEntitlements,
        linkedEntitlements: linkedEntitlementsToProcess,
        partnerPromotions,
        omsCapability,
        offeringsAndPricingPlans,
        isNonCommercialPricingEnabled: isNonCommercialPricingEnabled ?? false,
        currentPricingEligibility: currentPricingEligibility as PricingPlanType,
        currentCurrency,
      });

      return (appsPayload || []).concat(appsAmendmentPayloadForTransfer);
    })
  );

  return containerGroupedAppsPayloadArr.reduce((arr: OmsPayload['entitlements'], containerSpecificAppArr) => {
    if (containerSpecificAppArr && containerSpecificAppArr.length && arr) {
      arr = arr.concat(containerSpecificAppArr);
    }
    return arr;
  }, []);
}

function getContainerPayloadForNewJiraProducts({
  cartProducts,
  existingContainerIdMap,
  isJPDAsJiraFamilyEnabled,
}: {
  cartProducts: CartProductSchema[];
  existingContainerIdMap: Dictionary<CartProductSchema>;
  isJPDAsJiraFamilyEnabled?: boolean;
}): OmsPayload['entitlements'] {
  const newJiraProducts = cartProducts.filter(
    ({ productId, operationType }) =>
      isProductBelongsToJiraFamily(productId, { isJPDAsJiraFamilyEnabled }) && isNewEntitlement({ operationType })
  );

  const containerIdToDetailsMap = keyBy(newJiraProducts, 'containerId');

  const allContainerIds = Object.keys(containerIdToDetailsMap);

  return allContainerIds
    .map((containerId) => {
      // for existing container IDs, we don't need to send a new payload
      if (existingContainerIdMap[containerId]) {
        return null;
      }
      const productDetails = containerIdToDetailsMap[containerId];
      const cloudSiteId = productDetails.cloudSiteId;

      return {
        itemId: containerId,
        offeringId: CONTAINER_OFFERING_ID,
        entitlementId: (null as unknown) as string,
        orderType: OrderItemType.CREATION_ORDER,
        productId: CONTAINER_PRODUCT_ID,
        ...(cloudSiteId ? { metadata: { siteId: cloudSiteId } } : {}),
      };
    })
    .filter(truthy);
}

function getEligibleProductForContainerPayload({
  cartProducts,
  nextBillDate,
  isSandboxEligibleProducts,
}: {
  cartProducts: CartProductSchema[];
  nextBillDate: State['nextBillDate'];
  isSandboxEligibleProducts: boolean;
}) {
  const filteredCartProducts = isSandboxEligibleProducts
    ? cartProducts.filter((cartProduct) => cartProduct.isSandbox)
    : cartProducts.filter((cartProduct) => !cartProduct.isSandbox);
  return filteredCartProducts.filter((cartProduct) => {
    const { productId, operationType, productStatuses } = cartProduct;

    const isJira = isJiraProduct(productId);
    const isNew = isNewEntitlement({ operationType });

    const isTransfer = productStatuses?.includes(ProductStatus.TRANSFER);
    const isEdited = productStatuses?.includes(ProductStatus.EDITED);
    /**
     * for transfer + amendment, we orchestrate the orders on ANS side like this:
     * 1. transfer happens
     * 2. then amendment is triggered on transferred subscriptions
     *
     * Here, the amendment happens in the similar way we would do for amendment of existing products.
     */
    const isTransferWithoutAmendment = isTransfer && !isEdited;

    if (!isJira || isNew || isTransferWithoutAmendment) return false;

    const nextBillingAnchorTimestamp =
      nextBillDate.cloudSiteLevel?.[cartProduct.cloudSite] ||
      nextBillDate.cartLevel ||
      nextBillDate.entitlementLevel?.[cartProduct.id];

    // for existing jira products (amendment flow), if we are changing only the bill date, we need to pass a catalog object for the cascading changes to the other sibling products if any.
    return !!nextBillingAnchorTimestamp;
  });
}

function getContainerPayload({
  containerIds,
  containerIdToDetailsMap,
  nextBillDate,
  containerOfferingId,
}: {
  containerIds: string[];
  containerIdToDetailsMap: Dictionary<CartProductSchema>;
  nextBillDate: State['nextBillDate'];
  containerOfferingId: string;
}) {
  return containerIds.map((containerId) => {
    const productDetails = containerIdToDetailsMap[containerId];
    const nextBillingAnchorTimestamp =
      nextBillDate.cloudSiteLevel?.[productDetails.cloudSite] ||
      nextBillDate.cartLevel ||
      nextBillDate.entitlementLevel?.[productDetails.id];

    const existingPartnerPromotionIndex = productDetails?.promotions?.findIndex(
      ({ type }) => type === PARTNER_DISCOUNT_TYPE
    );

    const isTransferPartnerProduct =
      existingPartnerPromotionIndex >= 0 && productDetails.productStatuses?.includes(ProductStatus.TRANSFER);

    const metadataArg = isTransferPartnerProduct ? { metadata: { POELineItem: 'true' } } : {};

    return {
      itemId: containerId,
      offeringId: containerOfferingId,
      entitlementId: containerId,
      orderType: OrderItemType.AMENDMENT_ORDER,
      productId: CONTAINER_PRODUCT_ID,
      nextBillingAnchorTimestamp: Number(nextBillingAnchorTimestamp),
      ...metadataArg,
    };
  });
}

export function getContainerPayloadForBillDateChangeInExistingJiraProducts({
  cartProducts,
  nextBillDate,
}: {
  cartProducts: CartProductSchema[];
  nextBillDate: State['nextBillDate'];
}): OmsPayload['entitlements'] {
  const eligibleSandboxProducts = getEligibleProductForContainerPayload({
    cartProducts,
    nextBillDate,
    isSandboxEligibleProducts: true,
  });
  const eligibleNonSandboxProducts = getEligibleProductForContainerPayload({
    cartProducts,
    nextBillDate,
    isSandboxEligibleProducts: false,
  });

  const sandboxContainerIdToDetailsMap = keyBy(eligibleSandboxProducts, 'containerId');
  const nonSandboxContainerIdToDetailsMap = keyBy(eligibleNonSandboxProducts, 'containerId');

  const allSandboxContainerIds = Object.keys(sandboxContainerIdToDetailsMap);
  const allNonSandboxContainerIds = Object.keys(nonSandboxContainerIdToDetailsMap);

  return [
    ...getContainerPayload({
      containerIds: allSandboxContainerIds,
      containerIdToDetailsMap: sandboxContainerIdToDetailsMap,
      nextBillDate,
      containerOfferingId: SANDBOX_CONTAINER_OFFERING_ID,
    }),
    ...getContainerPayload({
      containerIds: allNonSandboxContainerIds,
      containerIdToDetailsMap: nonSandboxContainerIdToDetailsMap,
      nextBillDate,
      containerOfferingId: CONTAINER_OFFERING_ID,
    }),
  ];
}

function getTransferContainerPayload({
  cartProducts,
  isJPDAsJiraFamilyEnabled,
}: {
  cartProducts: CartProductSchema[];
  isJPDAsJiraFamilyEnabled?: boolean;
}): OmsPayload['entitlements'] {
  const transferJiraProducts = cartProducts.filter(
    ({ productStatuses, productId, offeringId }) =>
      productStatuses?.includes(ProductStatus.TRANSFER) &&
      isProductBelongsToJiraFamily(productId, {
        isJPDAsJiraFamilyEnabled: isJPDAsJiraFamilyEnabled && !isExistingStandaloneJPDProduct(offeringId),
      })
  );

  const containerEntitlementIdToDetailsMap = keyBy(transferJiraProducts, 'containerId');

  const allContainerEntitlementIds = Object.keys(containerEntitlementIdToDetailsMap);

  return allContainerEntitlementIds.map((containerId) => {
    const productDetails = containerEntitlementIdToDetailsMap[containerId];

    const isTransferPartnerProduct =
      productDetails.isPartnerEntitlement && productDetails.productStatuses?.includes(ProductStatus.TRANSFER);

    const metadata: Dictionary<string> = {
      sourceTransactionAccountId: productDetails.sourceTransactionAccountId ?? '',
      sourceInvoiceGroupId: productDetails.sourceInvoiceGroupId ?? '',
      // pass site id as metadata attribute to OMS engine
      siteId: productDetails.cloudSiteId,
    };
    if (isTransferPartnerProduct) {
      metadata['POELineItem'] = 'true';
    }

    return {
      itemId: generateUuid(),
      entitlementId: containerId as string,
      orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
      productId: CONTAINER_PRODUCT_ID,
      metadata,
    };
  });
}

export const getSkipTrialPayload = ({
  initialOfferingId,
  updatedOfferingId,
  productId,
  isCurrentlyInTrial,
  offeringsAndpricingPlans,
  omsCapability,
}: {
  initialOfferingId: string;
  updatedOfferingId: string;
  productId: CartProductSchema['productId'];
  isCurrentlyInTrial: boolean;
  offeringsAndpricingPlans: State['offeringsAndpricingPlans'];
  omsCapability: OmsCapability;
}) => {
  // TEMP: this is a temp fix for statuspage trial issue, we need to remove this once we have a proper fix
  if (!isCurrentlyInTrial && isStatuspage(productId)) {
    return {
      skipTrial: true,
    };
  }
  const isResultantEntitlementInTrial = getTrialStatusForEntitlement({
    currentOfferingId: initialOfferingId,
    updatedOfferingId,
    productId: productId,
    inCurrentlyInTrial: isCurrentlyInTrial,
    offeringsAndpricingPlans: offeringsAndpricingPlans || {},
  });
  return (() => {
    if (omsCapability === OmsCapability.BILL_ESTIMATE) {
      return {
        skipTrial: true,
      };
    }

    return {
      skipTrial: !isResultantEntitlementInTrial,
    };
  })();
};
/**
 * Relates from data need to send the relates from payload only when a new entitlement of
 * jira family is about to be added to new container or already existing container
 * This is happening for Bill estimation, Order Preview and Order requests
 * @param isNew
 * @param product
 * @param entitlementId
 * @param existingContainerIdMap
 */
export const getRelatesFromPayload = ({
  isNew,
  product,
  entitlementId,
  existingContainerIdMap,
  isJPDAsJiraFamilyEnabled,
}: {
  isNew?: boolean;
  product: CartProductSchema;
  entitlementId: string;
  existingContainerIdMap: Dictionary<CartProductSchema>;
  isJPDAsJiraFamilyEnabled?: boolean;
}) => {
  if (
    !isNew ||
    !isProductBelongsToJiraFamily(product.productId, {
      isJPDAsJiraFamilyEnabled: isJPDAsJiraFamilyEnabled && !isExistingStandaloneJPDProduct(product.offeringId),
    })
  ) {
    return {};
  }
  // in case the container id is already there, we need to send that with reference type as entitlement
  return product.containerId && existingContainerIdMap[product.containerId]
    ? {
        relatesFromEntitlements: [
          {
            entitlementId: product.containerId,
            referenceType: EntitlementReferenceType.ENTITLEMENT,
            relationshipType: generatedTypesNew.RelationshipPathType.FAMILY_CONTAINER,
          },
        ],
      }
    : {
        relatesFromEntitlements: [
          {
            itemId: product.containerId,
            entitlementId,
            referenceType: EntitlementReferenceType.ORDER_ITEM,
            relationshipType: generatedTypesNew.RelationshipPathType.FAMILY_CONTAINER,
          },
        ],
      };
};

/**
 * @param nextBillDate
 * @param product
 * @param app
 * @param existingContainerIdBillingAnchorTimestampMap
 * @param currentTxaId
 */
export const getNextBillingAnchorTimestampPayload = async ({
  nextBillDate,
  product,
  app,
  existingContainerIdBillingAnchorTimestampMap,
  selectedLinkedEntitlements,
  parentProduct,
  cartApps,
  initialStoreProducts,
  offeringsAndpricingPlans,
  isJPDAsJiraFamilyEnabled,
  isCoTermInUofEnabled,
}: {
  nextBillDate?: State['nextBillDate'];
  product?: CartProductSchema;
  app?: CartLinkedEntitlementsSchema;
  existingContainerIdBillingAnchorTimestampMap?: Dictionary<number>;
  selectedLinkedEntitlements?: State['selectedLinkedEntitlements'];
  parentProduct?: CartProductSchema;
  cartApps?: State['linkedEntitlements'];
  initialStoreProducts?: State['products'];
  offeringsAndpricingPlans?: State['offeringsAndpricingPlans'];
  isJPDAsJiraFamilyEnabled?: boolean;
  isCoTermInUofEnabled?: boolean;
}) => {
  if (!app && !product) {
    return;
  }

  // if user has entered any custom next bill date
  if (nextBillDate && Object.values(nextBillDate).length) {
    // setting same timestamp for all products and app (existing and new ones)
    if (nextBillDate.cartLevel) {
      // these are timestamps and not date strings;
      return Number(nextBillDate.cartLevel);
    }

    // setting same timestamp for each cloud site URL for all products and app (existing and new ones)
    if (product && nextBillDate.cloudSiteLevel?.[product.cloudSite]) {
      // these are timestamps and not date strings;
      return Number(nextBillDate.cloudSiteLevel?.[product.cloudSite]);
    }
    if (app && app?.cloudSite && nextBillDate.cloudSiteLevel?.[app.cloudSite]) {
      return Number(nextBillDate.cloudSiteLevel?.[app.cloudSite]);
    }

    if (product && nextBillDate.entitlementLevel?.[product.id]) {
      return Number(nextBillDate.entitlementLevel?.[product.id]);
    }

    if (app && app?.cloudSite && nextBillDate.entitlementLevel?.[app.id]) {
      return Number(nextBillDate.entitlementLevel?.[app.id]);
    }
  }

  if (app) {
    // For New app nextBillDate will be taken from cloudSite level if set
    // For existing selected app if nextBillDate is set read it from store or fallback to default app nextBillingTimestamp
    const isNewApp = app?.operationType === OperationType.NEW;
    // checks if parent product is available but no billing date changed should set nextBillingTimestamp from app as default
    // OR check if selected app has nextBillDate set
    const appNextBillDate =
      isNewApp && parentProduct?.cloudSite
        ? nextBillDate?.cloudSiteLevel?.[parentProduct?.cloudSite] ||
          nextBillDate?.entitlementLevel?.[parentProduct?.id]
        : selectedLinkedEntitlements?.[app.id]?.nextBillDate;

    /**
     * while placing the order for new products, we are checking the existing product's next billing anchor time
     * or latest billing date of the same cloudsite for the new product.
     */
    const existingNextBillDateAnchorTimeOfCloudSite = isCoTermInUofEnabled
      ? getExistingNextBillDateAnchorTimeFromCloudSite({
          initialStoreProducts,
          cloudSite: parentProduct?.cloudSite,
          nextBillDate,
        })
      : null;

    // for new apps we have default nextBillingTimestamp set but for existing apps we can use nextBillAnchorDate
    const nextBillingTimestamp = isNewApp ? app?.nextBillingTimestamp : app?.nextBillAnchorDate;

    const defaultNextBillingTimestamp = (() => {
      if (nextBillingTimestamp) return Number(nextBillingTimestamp);

      // checking the existing products for next billing anchor timestamp
      if (existingNextBillDateAnchorTimeOfCloudSite) return existingNextBillDateAnchorTimeOfCloudSite;

      return Number(Date.now());
    })();

    // if new app and no billing date is set send todays date as next bill anchor timestamp
    if (isNewApp && !appNextBillDate) {
      return defaultNextBillingTimestamp;
    } else {
      return appNextBillDate ? Number(appNextBillDate) : defaultNextBillingTimestamp;
    }
  }

  if (product) {
    // in case a new product is added on existing or new cloud-site
    if (isNewEntitlement(product)) {
      /**
       * while placing the order for new products, we are checking the existing product's next billing anchor time
       * or latest billing date of the same cloudsite for the new product.
       */
      const existingNextBillDateAnchorTimeOfCloudSite = isCoTermInUofEnabled
        ? getExistingNextBillDateAnchorTimeFromCloudSite({
            initialStoreProducts,
            cloudSite: product.cloudSite,
            nextBillDate,
          })
        : null;
      // for JIRA related products
      if (
        isProductBelongsToJiraFamily(product.productId, { isJPDAsJiraFamilyEnabled }) &&
        product.containerId &&
        existingContainerIdBillingAnchorTimestampMap
      ) {
        // if we have DO NOT have any product from same container in store which can be used for verifying
        if (!existingContainerIdBillingAnchorTimestampMap[product.containerId]) {
          // we don't need the support of defaults API as currently
          // we have the products in the container to give us the nextBillAnchorDate

          // const defaults = await getOrderDefaults({
          //   offeringId: product.offeringId,
          //   transactionAccountId: (currentTxaId || product.sourceTransactionAccountId) as string,
          //   // @ts-ignore
          //   containerId: product.containerId,
          // });
          //
          // const nextBillingTimestamp = defaults.billingAnchorTime?.value ?? +new Date();
          const nextBillingTimestamp = existingNextBillDateAnchorTimeOfCloudSite ?? Number(Date.now());
          existingContainerIdBillingAnchorTimestampMap[product.containerId] = nextBillingTimestamp;
          return nextBillingTimestamp;
        }
        // if we have any product from same container in store which can be used for verifying
        return existingContainerIdBillingAnchorTimestampMap[product.containerId];
      }

      // confluence and other products in case of new
      // event for Jira products on new cloud-site
      return existingNextBillDateAnchorTimeOfCloudSite ?? Number(Date.now());
    }

    /**
     * For existing products, we need to check if the nextBillAnchorDate is not blank before setting existing next bill anchor date as received.
     * Ideally nextBillAnchorDate is blank for Free product, but we need nextBillAnchorDate when amendment from Free to any other plan.
     * In this case we need to check if the nextBillAnchorDate is set in linked products or apps, copy same to the payload.
     */

    const initialOfferingId = initialStoreProducts?.[product.id]?.offeringId ?? '';

    const offerings = offeringsAndpricingPlans?.[product.productId] || [];
    const initialOfferingDetails = offerings?.find?.(({ id: offeringId }) => offeringId === initialOfferingId);
    const selectedOfferingDetails = offerings.find(({ id: offeringId }) => offeringId === product.offeringId);

    const isInitialOfferingFree = isFreeOffering(initialOfferingDetails?.pricingType ?? '');
    const isSelectedOfferingFree = isFreeOffering(selectedOfferingDetails?.pricingType ?? '');

    if (isInitialOfferingFree && !isSelectedOfferingFree) {
      if (product.id && cartApps && initialStoreProducts) {
        const linkedTimestamp = getTimestampFromLinkedProductsOrApps({
          parentEntitlementId: product.id,
          products: initialStoreProducts,
          linkedEntitlements: cartApps,
        });
        if (linkedTimestamp) {
          return Number(linkedTimestamp);
        }
      }
    }

    // setting existing next bill anchor date as received
    return Number(product.nextBillAnchorDate);
  }
};

const getEntitlementLineItems = async ({
  initialStoreProducts,
  offeringsAndpricingPlans,
  cartProducts,
  cartApps,
  nextBillDate,
  selectedProducts,
  invoiceGroupId,
  partnerPromotions = {},
  omsCapability,
  productsArr,
  selectedLinkedEntitlements,
  isMultipleIgChangeHandlingForOrderEnabled = false,
  currentPricingEligibility,
  isIGChangeForFreeEntitlementEnabled,
  shouldExcludeDiscretionaryDiscount = false,
  currentCurrency,
  isNonCommercialPricingEnabled = false,
  isJPDAsJiraFamilyEnabled,
  isCoTermInUofEnabled,
  isSandboxAppCreateInUOFEnabled,
  sandboxParentData,
  isExcludeExpiredPromotionEnabled,
  isAppEditionSupportEnabled,
}: Omit<GetOmsPayload, 'sandboxApps' | 'sandboxProducts'>) => {
  const existingContainerIdMap = keyBy(
    productsArr.filter((product) => product.operationType !== OperationType.NEW),
    'containerId'
  );

  const existingExternalContainerIdMap = keyBy(
    productsArr.filter((product) => product.isExternalProduct),
    'containerId'
  );

  const isBillEstimate = omsCapability === OmsCapability.BILL_ESTIMATE;

  const existingContainerIdBillingAnchorTimestampMap = productsArr
    .filter((product) => product.operationType !== OperationType.NEW)
    .reduce((acc: Record<string, number>, product: CartProductSchema) => {
      if (product.containerId) {
        acc[product.containerId] = Number(product.nextBillAnchorDate);
      }
      return acc;
    }, {});

  // filtering out products that donot have changes during order placement
  const eligibleCartProducts = isBillEstimate
    ? cartProducts
    : cartProducts.filter(({ productStatuses }) => !!productStatuses?.length);

  const transferWithAmendmentProducts = cartProducts.filter(
    ({ operationType, productStatuses }) =>
      operationType === OperationType.TRANSFER && productStatuses?.includes(ProductStatus.EDITED)
  );

  const transferWithAmendmentProductIdsMap = keyBy(transferWithAmendmentProducts, 'id');

  // FIXME :: Due to update in the Version of `getBillEstimateV2` method some issue has started
  //  coming up related to metadata field
  // @ts-ignore
  const entitlementsForOMS: OmsPayload['entitlements'] = await Promise.all(
    eligibleCartProducts.map(async (product) => {
      const isNew = isNewEntitlement(product);
      const isTransfer = product.operationType === OperationType.TRANSFER;
      const isBillEstimateForTransfer = isTransfer && isBillEstimate;

      const orderType = (() => {
        if (isNew || isBillEstimateForTransfer) return OrderItemType.CREATION_ORDER;

        if (isTransfer) return OrderItemType.ACCOUNT_MODIFICATION_ORDER;

        return OrderItemType.AMENDMENT_ORDER;
      })();

      const entitlementId = (isNew || isBillEstimateForTransfer ? null : product.id) as string; // this is temporary fix. We will remove once we are ready with bill estimate oms v2 for new product without entitlement

      // Generating uuid for existing products so that we can map the validation errors received from CCP to cart products. For new products, `product.id` is a uuid generated during product creation.
      const itemId = isNew || isBillEstimateForTransfer ? product.id : product.itemId;

      const cloudSiteId =
        product.cloudSiteId && product.cloudSiteType === CloudSiteTypeEnum.OWN ? product.cloudSiteId : '';

      const nextBillingAnchorTimestamp =
        omsCapability !== OmsCapability.BILL_ESTIMATE
          ? await getNextBillingAnchorTimestampPayload({
              nextBillDate,
              product,
              existingContainerIdBillingAnchorTimestampMap,
              cartApps,
              initialStoreProducts,
              offeringsAndpricingPlans,
              isJPDAsJiraFamilyEnabled,
              isCoTermInUofEnabled,
            })
          : null;

      const nextBillingAnchorTimestampArg = nextBillingAnchorTimestamp
        ? {
            nextBillingAnchorTimestamp: Number(nextBillingAnchorTimestamp),
          }
        : {};

      const partnerPromotionId = partnerPromotions[product.productId];
      let appliedPromotions =
        // check to add existing promotions only if its bill estimate and not the new entitlement
        // do not send expired promotions for bill estimation call
        isBillEstimate && !isNew && product?.promotions?.length
          ? product.promotions.filter((promotion) =>
              isRequiredPromotion(promotion, { excludeExpired: !!isExcludeExpiredPromotionEnabled })
            )
          : [];

      /** excluding all the discretionary discount from calculating estimate when flat discount support ff is enabled */
      appliedPromotions = shouldExcludeDiscretionaryDiscount
        ? appliedPromotions.filter((promotion) => promotion?.type !== PromotionTypes.DISCRETIONARY_DISCOUNT)
        : appliedPromotions;

      const initialOfferingId = initialStoreProducts?.[entitlementId]?.offeringId ?? '';
      const updatedOfferingId = product.offeringId;

      // For org product instance, we get non empty value for parentEntitlementId
      const isOrgInstance =
        product.parentEntitlementId &&
        isCollectionProductEntitlement(initialStoreProducts?.[product.parentEntitlementId].productId);

      const skipTrialArg = getSkipTrialPayload({
        initialOfferingId,
        updatedOfferingId,
        productId: product.productId,
        isCurrentlyInTrial: product.inTrial === true,
        offeringsAndpricingPlans: offeringsAndpricingPlans || {},
        omsCapability: omsCapability,
      });

      /**
       * For Org instances, skip trial is dependant on the parent product's trial status.
       * Hence, we need to send the skip trial payload as per the parent product.
       */
      let skipTrialArgsForInstance = {};
      // isOrgInstance is already checking for parentEntitlementId,
      // but adding this check to confirm it is not null for places used withing this block
      if (isOrgInstance && product.parentEntitlementId) {
        const parentProduct = cartProducts.find(({ id }) => id === product.parentEntitlementId);
        skipTrialArgsForInstance = getSkipTrialPayload({
          initialOfferingId: initialStoreProducts?.[product.parentEntitlementId]?.offeringId ?? '',
          updatedOfferingId: parentProduct?.offeringId ?? '',
          productId: parentProduct?.productId ?? '',
          isCurrentlyInTrial: parentProduct?.inTrial === true,
          offeringsAndpricingPlans: offeringsAndpricingPlans || {},
          omsCapability: omsCapability,
        });
      }

      const offerings = offeringsAndpricingPlans?.[product.productId] || [];
      const initialOfferingDetails = offerings?.find?.(({ id: offeringId }) => offeringId === initialOfferingId);
      const selectedOfferingDetails = offerings.find(({ id: offeringId }) => offeringId === product.offeringId);

      const isInitialOfferingFree = isFreeOffering(initialOfferingDetails?.pricingType ?? '');
      const isSelectedOfferingFree = isFreeOffering(selectedOfferingDetails?.pricingType ?? '');

      // finding the pricing plan id based on the pricing eligibility and offering
      const pricingPlanIdBasedOnPricingEligibilityAndOffering = (() => {
        if (currentPricingEligibility && currentCurrency) {
          const pricingPlanId = getPricingPlanId({
            pricingEligibility: currentPricingEligibility,
            currency: currentCurrency,
            offerings,
            offeringId: product.offeringId,
            defaultPricingEligibilityToCommercial: isNonCommercialPricingEnabled,
            enableRetainPricingPlan: isNonCommercialPricingEnabled,
            initialPricingPlanId: product.pricingPlanId,
            initialOfferingId: initialStoreProducts?.[product.id]?.offeringId ?? '',
            pricingPlanType: product.pricingPlanType,
            entitlementPriceEligibility: product.priceEligibility,
          });
          if (pricingPlanId) return pricingPlanId;
        }
        return product.pricingPlanId;
      })();

      const pricingPlanTypeBasedOnPricingPlanId = selectedOfferingDetails?.pricingPlans?.find(
        (p) => p.id === pricingPlanIdBasedOnPricingEligibilityAndOffering
      )?.type as `${PricingPlanType}`;

      const shouldAddPromotions = (() => {
        // skip sending promotions if the selected offering is `free`
        if (isSelectedOfferingFree) return false;

        if (isBillEstimate) return true;

        // user is upgrading from free to paid offering. need to add partner promotions for the same
        if (isInitialOfferingFree && !isSelectedOfferingFree) {
          return true;
        }

        return (
          partnerPromotionId &&
          !isEmpty(pricingPlanIdBasedOnPricingEligibilityAndOffering) &&
          orderType !== OrderItemType.AMENDMENT_ORDER
        );
      })();

      const isImmediateAmendmentToFreeArg = (() => {
        // selected offering is NOT free, we need NOT send the `isImmediateAmendmentToFree` flag
        if (!isSelectedOfferingFree) return {};

        // selected offering is free, but we are generating bill estimate payload where it is considered as a creation flow, we need NOT send the `isImmediateAmendmentToFree` flag
        if (isBillEstimate) return {};

        // selected offering is free and order type is amendment, we need to send the `isImmediateAmendmentToFree` flag
        if (
          orderType === OrderItemType.AMENDMENT_ORDER &&
          product.cloudSiteType === CloudSiteTypeEnum.OWN &&
          !isOrgInstance &&
          !isCollectionProductEntitlement(product.productId)
        ) {
          return {
            isImmediateAmendmentToFree: true,
          };
        }

        // selected offering is free and order type is NOT amendment, we need NOT send the `isImmediateAmendmentToFree` flag
        return {};
      })();

      const removePromotions = product?.removePromotions ?? []; // removePromotions is an array of promotionInstanceId
      const removePromotionsArg = removePromotions?.length ? { removePromotions } : {};

      //for bill estimate, we need to remove the promotions which are removed by user
      if (omsCapability === OmsCapability.BILL_ESTIMATE) {
        appliedPromotions = appliedPromotions?.filter((promotion) => {
          // check if the promotion is removed by user
          const isPromotionToRemove = removePromotions?.some(
            (promo) => promo?.promotionInstanceId === promotion?.promotionInstanceId
          );
          return !isPromotionToRemove;
        });
      }

      const addPromotions = appliedPromotions
        ?.filter((promotion) => !isEmpty(promotion))
        ?.map((promotion) => {
          const promotionId = promotion?.id;
          return {
            promotionInstanceId: null,
            promotionDefinition: {
              promotionId: promotionId,
              promotionCode: null,
              customisedValues: null,
            },
          };
        });

      if (partnerPromotionId) {
        addPromotions.push({
          promotionInstanceId: null,
          promotionDefinition: {
            promotionId: partnerPromotionId,
            promotionCode: null,
            customisedValues: null,
          },
        });
      }

      const addPromotionsArg = shouldAddPromotions ? { addPromotions } : {};

      const containerRelatesFromEntitlementsPayload = getRelatesFromPayload({
        isNew,
        product,
        entitlementId,
        existingContainerIdMap: {
          ...existingContainerIdMap,
          ...existingExternalContainerIdMap,
        },
        isJPDAsJiraFamilyEnabled,
      });

      const metadata = (() => {
        const isProductTransferAmendment = !!transferWithAmendmentProductIdsMap[product.id];
        // Ref. Doc - https://hello.atlassian.net/wiki/spaces/~686306312/pages/3044230860/EPIC+Pager+Patch+promotions+for+Transfer+Amendment+orders
        // Sending this flag to prevent POE make changes on entitlement only when entitlement is already on partner account
        // Pass AccntModificationCumAmendmentFlow as true to skip POE processing for D2P (direct to partner) or P2P (partner to partner) transfer + amendment
        const transferAmendmentPayloadArg = {
          AccntModificationCumAmendmentFlow: 'true',
        };
        const POELineItemArg = product.isPartnerEntitlement ? { POELineItem: 'true' } : {};
        if (isTransfer) {
          return {
            sourceTransactionAccountId: product.sourceTransactionAccountId,
            sourceInvoiceGroupId: product.sourceInvoiceGroupId ?? '',
            // pass site id as metadata attribute to OMS engine
            siteId: product.cloudSiteId,
            ...transferAmendmentPayloadArg,
            ...POELineItemArg,
          };
        }

        /**
         * We need to send POELineItem: 'true' for the new entitlements which are being added to
         * the existing Family products selected to transfer and the transferred product is a partner entitlement.
         * This need to be done only for coupled product in transfer.
         */
        if (
          cloudSiteId &&
          isProductBelongsToJiraFamily(product.productId, { isJPDAsJiraFamilyEnabled }) &&
          orderType === OrderItemType.CREATION_ORDER &&
          product.containerId
        ) {
          const existingFamilyProductForTransfer = groupBy(
            productsArr.filter((product) => product.operationType === OperationType.TRANSFER),
            'containerId'
          );

          let isProductInExistingContainerForTransfer = false;
          let isParentProductPartnerEntitlement = false;
          if (existingFamilyProductForTransfer) {
            const familyProducts = existingFamilyProductForTransfer[product.containerId];
            isProductInExistingContainerForTransfer = !!familyProducts?.find(
              (familyProduct) => familyProduct.containerId === product.containerId
            );
            isParentProductPartnerEntitlement = !!familyProducts?.find(
              (familyProduct) => familyProduct.isPartnerEntitlement
            );
          }

          if (isProductInExistingContainerForTransfer && isParentProductPartnerEntitlement) {
            return {
              siteId: cloudSiteId,
              POELineItem: 'true',
            };
          }
        }

        if (cloudSiteId) {
          return {
            // pass site id as metadata attribute to OMS engine
            siteId: cloudSiteId,
          };
        }

        return {};
      })();

      const isPreviewOrOrderPlacementFlow = [OmsCapability.ORDER_PREVIEW, OmsCapability.ORDER_PLACEMENT].includes(
        omsCapability
      );

      // check if the only change in entitlement is in bill date
      const isBillDateTheOnlyChange = (() => {
        if (isNew || isTransfer) return false;

        // Now next bill date will be added to changed products hash.
        // We are still checking on the global store level change because it is not handled for all use-cases:
        // product addition after setting cloud-site level bill date.
        const otherChanges = omit(selectedProducts[product.id], ['productStatuses', 'nextBillDate']);
        const isPricingPlanChange = pricingPlanIdBasedOnPricingEligibilityAndOffering !== product.pricingPlanId;
        return nextBillingAnchorTimestamp && isEmpty(otherChanges) && !isPricingPlanChange;
      })();

      if (isBillDateTheOnlyChange && isPreviewOrOrderPlacementFlow) {
        return {
          entitlementId,
          orderType,
          productId: product.productId,
          offeringId: product.offeringId,
          ...nextBillingAnchorTimestampArg,
        };
      }

      // Update chargeQuantity as 1 for new Entitlements
      let chargeQuantity = Number(product.activeUsers);
      if (omsCapability !== OmsCapability.BILL_ESTIMATE && orderType === OrderItemType.CREATION_ORDER) {
        chargeQuantity = 1;
      }

      return {
        entitlementId,
        itemId,
        productId: product.productId,
        pricingPlanType: isNonCommercialPricingEnabled
          ? pricingPlanTypeBasedOnPricingPlanId
          : currentPricingEligibility ?? CCPPricing.COMMERCIAL,
        ...(isOrgInstance ? skipTrialArgsForInstance : skipTrialArg),
        offeringId: product.offeringId,
        orderType: orderType,
        pricingPlanId: pricingPlanIdBasedOnPricingEligibilityAndOffering,
        chargeQuantity: chargeQuantity,
        chargeElement: getChargeElement(product.productId),
        ...addPromotionsArg,
        ...removePromotionsArg,
        ...containerRelatesFromEntitlementsPayload,
        metadata,
        ...nextBillingAnchorTimestampArg,
        ...isImmediateAmendmentToFreeArg,
      };
    })
  );

  const appEntitlements =
    (await getAppsForOms({
      initialStoreProducts,
      cartProducts,
      cartApps,
      omsCapability,
      selectedLinkedEntitlements,
      partnerPromotions,
      nextBillDate,
      currentPricingEligibility,
      offeringsAndPricingPlans: offeringsAndpricingPlans,
      currentCurrency,
      isNonCommercialPricingEnabled,
      isCoTermInUofEnabled,
      isSandboxAppCreateInUOFEnabled,
      sandboxParentData,
      isExcludeExpiredPromotionEnabled,
      isAppEditionSupportEnabled,
    })) || [];

  const amendmentWithDestinationIgChangePayload = (() => {
    if (isMultipleIgChangeHandlingForOrderEnabled && omsCapability !== OmsCapability.BILL_ESTIMATE) {
      const lineItemsForDestinationBillingProfileChange = getLineItemsForDestinationBillingProfileChange({
        initialStoreProducts,
        invoiceGroupId,
        selectedProducts,
        cartApps,
        isIGChangeForFreeEntitlementEnabled,
      });
      return lineItemsForDestinationBillingProfileChange || [];
    }

    return [];
  })();

  const containerPayload =
    getContainerPayloadForNewJiraProducts({
      cartProducts,
      existingContainerIdMap: {
        ...existingContainerIdMap,
        ...existingExternalContainerIdMap,
      },
      isJPDAsJiraFamilyEnabled,
    }) || [];
  const transferContainerPayload =
    omsCapability === OmsCapability.BILL_ESTIMATE
      ? []
      : getTransferContainerPayload({ cartProducts, isJPDAsJiraFamilyEnabled }) || [];
  const billDateChangeContainerPayload =
    getContainerPayloadForBillDateChangeInExistingJiraProducts({ cartProducts, nextBillDate }) || [];

  const amendmentPayloadFromTransferType =
    getProductsAmendmentAndPatchPayloadFromTransfer({
      initialStoreProducts,
      entitlementsForOMS,
      cartProducts,
      selectedProducts,
      omsCapability,
      offeringsAndpricingPlans,
      partnerPromotions,
      isNonCommercialPricingEnabled,
      currentPricingEligibility,
      currentCurrency,
    }) || [];

  return (
    entitlementsForOMS?.concat(
      appEntitlements,
      containerPayload,
      billDateChangeContainerPayload,
      transferContainerPayload,
      amendmentPayloadFromTransferType,
      amendmentWithDestinationIgChangePayload
    ) ?? []
  );
};

export async function getOmsPayload({
  initialStoreProducts,
  offeringsAndpricingPlans,
  cartProducts,
  cartApps,
  nextBillDate,
  selectedProducts,
  transactionAccountId,
  invoiceGroupId,
  partnerPromotions = {},
  omsCapability,
  endCustomerContacts = [],
  // we are relying on the all products data from store for validation
  // FIXME :: In case of Deactivated or different ECC contact, we will get issues as those products are not there in store
  productsArr,
  selectedLinkedEntitlements,
  isMultipleIgChangeHandlingForOrderEnabled = false,
  currentPricingEligibility,
  isIGChangeForFreeEntitlementEnabled,
  isECCInMetaDataDisabled = false,
  shouldExcludeDiscretionaryDiscount = false,
  sandboxProducts,
  sandboxApps,
  currentCurrency,
  isNonCommercialPricingEnabled = false,
  isJPDAsJiraFamilyEnabled = false,
  isCoTermInUofEnabled = false,
  isSandboxAppCreateInUOFEnabled = false,
  sandboxParentData,
  isExcludeExpiredPromotionEnabled = false,
  isAppEditionSupportEnabled = false,
}: GetOmsPayload): Promise<OmsPayload> {
  const entitlementLineItemPayload = {
    initialStoreProducts,
    offeringsAndpricingPlans,
    cartProducts,
    cartApps,
    nextBillDate,
    selectedProducts,
    transactionAccountId,
    invoiceGroupId,
    partnerPromotions,
    omsCapability,
    endCustomerContacts,
    productsArr,
    selectedLinkedEntitlements,
    isMultipleIgChangeHandlingForOrderEnabled,
    currentPricingEligibility,
    isIGChangeForFreeEntitlementEnabled,
    isECCInMetaDataDisabled,
    shouldExcludeDiscretionaryDiscount,
    currentCurrency,
    isNonCommercialPricingEnabled,
    isJPDAsJiraFamilyEnabled,
    isCoTermInUofEnabled,
    isSandboxAppCreateInUOFEnabled,
    sandboxParentData,
    isExcludeExpiredPromotionEnabled,
    isAppEditionSupportEnabled,
  };

  const entitlementLineItems = await getEntitlementLineItems(entitlementLineItemPayload);
  let sandboxLineItems: any[] = [];
  if (sandboxProducts && Object.keys(sandboxProducts).length && cartProducts.length !== 0) {
    sandboxLineItems = await getEntitlementLineItems({
      ...entitlementLineItemPayload,
      ...getSandboxOmsPayload({
        cartProducts: cartProducts,
        sandboxProducts: sandboxProducts,
        sandboxApps: sandboxApps,
      }),
    });
  }

  const metadata: Dictionary<string> = {
    splitOrder: SplitOrder.Y, // ANS OMS v2 engine to split orders to mitigate CCP limitations
  };

  if (!isECCInMetaDataDisabled) {
    metadata['ecc_aaids'] = endCustomerContacts.join(', ');
  }

  return {
    transactionAccountId,
    invoiceGroupId,
    metadata,
    entitlements: [...entitlementLineItems, ...sandboxLineItems],
  };
}

export function getEntitlementIdForItemId(omsPayload: OmsPayload, itemId: string): CartProductSchema['id'] {
  const entitlements = omsPayload?.entitlements ?? [];
  // @ts-ignore
  const entitlement = entitlements.find((e) => e.itemId === itemId);
  return entitlement?.entitlementId ?? '';
}

export const getProductStatuses = (
  selectedProductDiff: Partial<CartProductSchema | CartLinkedEntitlementsSchema>,
  initialProduct: CartProductSchema | CartLinkedEntitlementsSchema
): ProductStatus[] => {
  if (initialProduct.operationType === OperationType.NEW) {
    return [ProductStatus.NEW];
  }
  // remove "productStatuses" key to check if any edit is there on object
  const changedSelectedProduct = omit(selectedProductDiff, 'productStatuses');
  const isProductEdited = !!Object.keys(changedSelectedProduct).length;

  if (initialProduct.operationType === OperationType.TRANSFER) {
    const status = [ProductStatus.TRANSFER];
    if (isProductEdited) {
      status.push(ProductStatus.EDITED);
    }
    return status;
  }
  if (isProductEdited) {
    return [ProductStatus.EDITED];
  }
  return [];
};

/**
 * Get the identifier for the app on store, it can be product id or container id
 * @param product
 */
export const getAppIdentifier = (product: CartProductSchema): string => {
  // apps are identified with container id when product is jira and product id when product is non jira
  return isJiraProduct(product.productId) ? (product?.containerId as string) : product?.id;
};
export const getRemovedProductsAndAppsFromStore = ({
  products,
  linkedEntitlements,
  selectedProducts,
  productIdsToRemove,
  selectedLinkedEntitlements,
  appIdsToRemove,
  selectedApp,
  showSandboxProducts,
  isSandboxDecouplingEnabled,
}: Pick<State, 'products' | 'linkedEntitlements' | 'selectedProducts' | 'selectedLinkedEntitlements'> & {
  productIdsToRemove: string[];
  appIdsToRemove: string[];
  selectedApp?: CartLinkedEntitlementsSchema;
  showSandboxProducts?: boolean;
  isSandboxDecouplingEnabled?: boolean;
}): Pick<State, 'products' | 'linkedEntitlements' | 'selectedProducts' | 'selectedLinkedEntitlements'> => {
  productIdsToRemove.forEach((id) => {
    delete selectedProducts[id];
    // apps are identified with container id when product is jira and product id when product is non jira
    const containerOrProductId = getAppIdentifier(products[id]);

    if (isNewEntitlement(products[id])) {
      delete products[id];
      const isOtherProductFromContainerInCart =
        Object.keys(selectedProducts)?.some((id) => products[id].containerId === containerOrProductId) ?? false;
      if (!isOtherProductFromContainerInCart) {
        /**
         * Removing all apps linked to new product from selectedLinkedEntitlements in store.
         * Removing only new apps linked to new product from Apps in store and not existing apps.
         */
        const appsLinkedToRemovedProduct = linkedEntitlements[containerOrProductId] || [];
        appsLinkedToRemovedProduct.forEach((app) => {
          const idOfAppToDelete = app.id;
          const isNewApp = isNewEntitlement(app) ?? false;
          if (isNewApp) {
            delete selectedLinkedEntitlements?.[idOfAppToDelete];
            linkedEntitlements[containerOrProductId] = linkedEntitlements[containerOrProductId].filter(
              (app) => app.id !== idOfAppToDelete
            );
          } else {
            delete selectedLinkedEntitlements?.[idOfAppToDelete];
          }
        });
      }
    } else {
      if (showSandboxProducts && !isSandboxDecouplingEnabled) {
        /**
         * If any sandbox product present, remove in selectedProduct.
         * If any parent to sandbox product present, remove in selectedProduct.
         */

        const productIdOrContainerId = products[id]?.containerId ?? id;
        const allSandboxProductIdsInCart = getSandboxProductIdsInCart(products, productIdOrContainerId);
        allSandboxProductIdsInCart.forEach((sandboxProductId) => {
          delete selectedProducts[sandboxProductId];
        });
        // If product is a sandbox product, remove its parent product in selectedProductIdSet
        const sandboxParentEntOrContainerId = products[id]?.sandboxParent;
        if (sandboxParentEntOrContainerId) {
          const sandboxParentProductId = getParentIdForSandboxProductInCart(products, sandboxParentEntOrContainerId);
          sandboxParentProductId.forEach((sandboxParentId) => {
            delete selectedProducts[sandboxParentId];
          });
        }
      }

      /**
       * for existing products,
       * we need to remove the linked apps from selection and from store as a whole if they are new apps
       */
      const appsLinkedToRemovedProduct = linkedEntitlements[containerOrProductId] || [];

      /**
       * Below code is added to fix below issue:
       * Issue: For a family container, if we add a new app in the container,
       * on removing any product from cart in the same container,
       * the new app is also getting removed, even though other product from container are available.
       * Solution: We are checking if any other product from the same container is selected,
       * then we are not removing the new app from the cart.
       */
      const isOtherProductFromContainerInCart =
        Object.keys(selectedProducts)?.some((id) => products[id].containerId === containerOrProductId) ?? false;

      appsLinkedToRemovedProduct.forEach((app) => {
        const idOfAppToDelete = app.id;

        /**
         * reading `productStatuses` directly from `app` and not from `selectedLinkedEntitlements[app.id]` because new apps
         * when added to cart will have `NEW` in their initial `productStatuses` property.
         */
        const isNewApp = app.productStatuses?.includes(ProductStatus.NEW) ?? false;

        if (isNewApp) {
          if (!isOtherProductFromContainerInCart) {
            delete selectedLinkedEntitlements?.[idOfAppToDelete];
          }
        } else {
          delete selectedLinkedEntitlements?.[idOfAppToDelete];
        }

        // if the app linked to the removed product is new, we need to remove it from the store state as a whole
        if (isNewApp) {
          if (!isOtherProductFromContainerInCart) {
            linkedEntitlements[containerOrProductId] = linkedEntitlements[containerOrProductId].filter(
              (app) => app.id !== idOfAppToDelete
            );
          }
        }
      });
    }
  });

  const isNewApp = selectedApp?.productStatuses?.includes(ProductStatus.NEW) ?? false;
  appIdsToRemove.forEach((id) => {
    delete selectedLinkedEntitlements?.[id];
    if (isNewApp && selectedApp) {
      // apps are identified with container id when product is jira and product id when product is non jira
      const containerOrProductId = selectedApp?.containerOrProductId;
      linkedEntitlements[containerOrProductId] = linkedEntitlements[containerOrProductId].filter(
        (app) => app.id !== id
      );
    }
  });

  return { products, linkedEntitlements, selectedProducts, selectedLinkedEntitlements };
};

export const getShallowDiffObject = <S extends Record<string, any>, T extends Record<string, any>>(
  sourceObj: S,
  targetObj: T,
  ignoreKeys?: string[]
) => {
  const result: Record<string, any> = {};
  Object.keys(targetObj).forEach((key) => {
    if (!ignoreKeys?.includes(key) && targetObj[key] !== sourceObj?.[key]) {
      result[key] = targetObj[key];
    }
  });
  return result;
};

export const getShallowDiffKeyMappedObject = (
  sourceObj: Record<string, Record<string, any>>,
  targetObj: Record<string, Record<string, any>>
) => {
  const result: Record<string, Record<string, any>> = {};
  Object.keys(targetObj).forEach((key) => {
    result[key] = getShallowDiffObject(sourceObj[key], targetObj[key]);
  });
  return result;
};

export const stitchChangeKeyObjects = <U extends Record<string, any>>(
  sourceMap: Record<string, U>,
  changeMap?: Record<string, Partial<U>>
) => {
  const resultMap: Record<string, U> = {};
  if (changeMap) {
    Object.keys(changeMap).forEach((key) => {
      resultMap[key] = {
        ...sourceMap[key],
        ...changeMap[key],
      };
    });
  }
  return resultMap;
};

const getLinkedAppsMap = (
  linkedEntitlements: Record<CartLinkedEntitlementsSchema['containerOrProductId'], CartLinkedEntitlementsSchema[]>
): Record<string, CartLinkedEntitlementsSchema> => {
  const linkedEntitlementsMap: Record<string, CartLinkedEntitlementsSchema> = {};

  Object.keys(linkedEntitlements).forEach((containerOrProductId) => {
    const entitlements = linkedEntitlements[containerOrProductId];
    Object.keys(entitlements).forEach((appKey: any) => {
      const entitlement = entitlements[appKey];
      if (entitlement.relationshipType === LinkedEntitlementRelationShipType.APP) {
        linkedEntitlementsMap[entitlement.id] = entitlement;
      }
    });
  });

  return linkedEntitlementsMap;
};

export const transformAndStitchAppChangedKeyObjects = (
  linkedEntitlements: Record<CartLinkedEntitlementsSchema['containerOrProductId'], CartLinkedEntitlementsSchema[]>,
  selectedLinkedEntitlements:
    | Record<CartLinkedEntitlementsSchema['id'], Partial<CartLinkedEntitlementsSchema>>
    | undefined
) => {
  const mappedChangedMap: Record<string, CartLinkedEntitlementsSchema> = {};

  Object.keys(linkedEntitlements).forEach((containerOrProductId) => {
    const entitlements = linkedEntitlements[containerOrProductId];

    Object.keys(entitlements).forEach((appKey: any) => {
      const entitlement = entitlements[appKey];
      if (entitlement.relationshipType === LinkedEntitlementRelationShipType.APP) {
        const updatedFields = selectedLinkedEntitlements?.[entitlement.id];
        mappedChangedMap[entitlement.id] = entitlement;
        if (updatedFields) {
          mappedChangedMap[entitlement.id] = {
            ...entitlement,
            ...updatedFields,
          };
        } else {
          mappedChangedMap[entitlement.id] = entitlement;
        }
      }
    });
  });
  return stitchChangeKeyObjects(linkedEntitlements, mappedChangedMap as any);
};

export const getAppsIdsForSelectedContainer = (
  linkedEntitlements: State['linkedEntitlements'],
  selectedContainerIds: string[]
) => {
  const allApps = Object.values(linkedEntitlements);
  return allApps
    ?.flatMap((item) => item)
    ?.filter((app) => selectedContainerIds.includes(app.containerOrProductId))
    ?.map((app) => app.id);
};

/**
 * This method helps in creating a selectedProducts payload for the store by
 * stitching the changes
 * @param initialProducts
 * @param selectedProducts
 * @param identifier
 * @param changeObj
 * @param ignoreKeys
 */
export const getSelectedProductsAppsObj = ({
  initialProducts,
  selectedProducts,
  identifier,
  changeObj,
  ignoreKeys = ['productStatuses'],
}: {
  initialProducts: Record<string, CartProductSchema | CartLinkedEntitlementsSchema>;
  selectedProducts: Record<string, Partial<CartProductSchema | CartLinkedEntitlementsSchema>>;
  identifier: string | number;
  changeObj: Partial<CartProductSchema | CartLinkedEntitlementsSchema>;
  ignoreKeys?: Array<string>;
}) => {
  const selectedProductDiffObj = initialProducts[identifier]
    ? getShallowDiffObject(
        initialProducts[identifier],
        {
          ...selectedProducts[identifier],
          ...changeObj,
        },
        ignoreKeys
      )
    : {};

  const productStatuses =
    initialProducts[identifier] && getProductStatuses(selectedProductDiffObj, initialProducts[identifier]);

  return {
    ...selectedProducts,
    ...(initialProducts[identifier] && {
      [identifier]: {
        ...selectedProductDiffObj,
        productStatuses,
      },
    }),
  };
};

/**
 * Updates the selected linked entitlements in the store with the changed fields for apps.
 */

export const getSelectedAppsDetails = ({
  linkedEntitlements,
  selectedLinkedEntitlements,
  identifier,
  changeObj,
  ignoreKeys,
  offeringsAndpricingPlans,
}: {
  linkedEntitlements: Record<CartLinkedEntitlementsSchema['containerOrProductId'], CartLinkedEntitlementsSchema[]>;
  selectedLinkedEntitlements:
    | Record<CartLinkedEntitlementsSchema['id'], Partial<CartLinkedEntitlementsSchema>>
    | undefined;
  identifier: string | number;
  changeObj: Partial<CartProductSchema | CartLinkedEntitlementsSchema>;
  ignoreKeys?: Array<string>;
  offeringsAndpricingPlans?: State['offeringsAndpricingPlans'];
}) => {
  const appItemsMap: Record<string, CartLinkedEntitlementsSchema> = getLinkedAppsMap(linkedEntitlements);
  let updatedChangeObj = changeObj;

  Object.keys(appItemsMap).forEach((entitlement: any) => {
    if (isNewEntitlement(entitlement) && offeringsAndpricingPlans) {
      const offeringDetails = offeringsAndpricingPlans?.[entitlement?.id]?.find(
        (offering) => offering?.id === changeObj.offeringId
      );
      const trialLengthInDays = offeringDetails?.trialLengthInDays;
      if (trialLengthInDays) {
        updatedChangeObj = {
          ...updatedChangeObj,
          trialLengthInDays,
        };
      }
    }
  });

  return getSelectedProductsAppsObj({
    initialProducts: appItemsMap,
    selectedProducts: selectedLinkedEntitlements as any,
    identifier: identifier,
    changeObj: updatedChangeObj,
    ignoreKeys,
  });
};

export const getSelectedAppsObj = (state: State) => {
  const selectedLinkedEntitlements = { ...state.selectedLinkedEntitlements };
  if (isEmpty(selectedLinkedEntitlements)) {
    return undefined;
  }
  const allApps = Object.values(state.linkedEntitlements);
  const resultApps: Record<string, CartLinkedEntitlementsSchema> = {};
  const flatAppsList = allApps?.flatMap((item) => item);
  Object.keys(selectedLinkedEntitlements)?.forEach((appId) => {
    const selectedAppData = flatAppsList?.find((app) => app.id === appId);
    if (selectedAppData) resultApps[appId] = selectedAppData;
  });
  return resultApps;
};

export const getSelectedAppsData = (state: State): CartLinkedEntitlementsSchema[] | undefined => {
  const stichedAppsObj = getSelectedAppsObj(state);
  return stichedAppsObj ? Object.values(stichedAppsObj) : undefined;
};

export const getSelectedBillingProfile = (state: State) => {
  const { billingProfiles } = state;
  const currentBillingProfile = billingProfiles?.find((profile) => profile.id === state.invoiceGroupId);
  return currentBillingProfile;
};

export const getSandboxProductIdsInCart = (products: State['products'], productIdOrContainerId: string) => {
  if (!productIdOrContainerId) {
    return [];
  }
  const linkedSandboxProductsInCart: string[] = [];
  Object.values(products).forEach((product) => {
    if (product.sandboxParent === productIdOrContainerId && product.productId !== ROVO_PRODUCT_ID) {
      linkedSandboxProductsInCart.push(product.id);
    }
  });

  return linkedSandboxProductsInCart;
};

export const getParentIdForSandboxProductInCart = (
  products: State['products'],
  sandboxParentEntOrContainerId: string
) => {
  const linkedSandboxParentProductInCart: string[] = [];
  Object.values(products).forEach((product) => {
    if (product.id === sandboxParentEntOrContainerId || product.containerId === sandboxParentEntOrContainerId) {
      linkedSandboxParentProductInCart.push(product.id);
    }
  });

  return linkedSandboxParentProductInCart;
};
